import React from 'react';
import { FormField, Select, ColorPicker } from '../ui';
import { DesignConfig, FONT_FAMILIES, BORDER_RADIUSES, STYLE_PRESETS } from '../../types';

function getContrastTextColor(bgHex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(bgHex);
  if (!result) return '#000000';
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

interface DesignConfigFormProps {
  data: DesignConfig;
  onChange: (data: Partial<DesignConfig>) => void;
  errors: Record<string, string>;
}

const fontOptions = FONT_FAMILIES.map((font) => ({
  value: font,
  label: font,
}));

const radiusOptions = BORDER_RADIUSES.map((radius) => ({
  value: radius,
  label: {
    none: 'None (Sharp corners)',
    sm: 'Small (4px)',
    md: 'Medium (8px)',
    lg: 'Large (16px)',
  }[radius],
}));

const styleOptions = STYLE_PRESETS.map((style) => ({
  value: style,
  label: {
    corporate: 'Corporate - Clean professional',
    'minimal-light': 'Minimal Light - Airy, modern',
    gradient: 'Gradient - Bold, colorful',
    nature: 'Nature - Earthy, organic',
    luxury: 'Luxury - Gold, dark elegance',
    modern: 'Modern - Glassmorphism-lite',
  }[style],
}));

export const DesignConfigForm: React.FC<DesignConfigFormProps> = React.memo(({
  data,
  onChange,
  errors,
}) => {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-8" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <div className="flex flex-col gap-6">
          <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>Colors</h3>

          <FormField
            label="Primary Color"
            error={errors.primaryColor}
            hint="Main brand color for buttons and accents"
          >
            <ColorPicker
              value={data.primaryColor}
              onChange={(color) => onChange({ primaryColor: color })}
              error={errors.primaryColor}
              showContrastChecker
              contrastAgainst="#ffffff"
            />
          </FormField>

          <FormField
            label="Secondary Color"
            error={errors.secondaryColor}
            hint="Supporting color for backgrounds and text"
          >
            <ColorPicker
              value={data.secondaryColor}
              onChange={(color) => onChange({ secondaryColor: color })}
              error={errors.secondaryColor}
            />
          </FormField>

          <FormField
            label="Accent Color"
            error={errors.accentColor}
            hint="Highlight color for CTAs and important elements"
          >
            <ColorPicker
              value={data.accentColor}
              onChange={(color) => onChange({ accentColor: color })}
              error={errors.accentColor}
              showContrastChecker
              contrastAgainst="#ffffff"
            />
          </FormField>
        </div>

        <div className="flex flex-col gap-6">
          <h3 style={{ fontSize: 'var(--text-lg)', marginBottom: 'var(--space-2)' }}>Typography & Style</h3>

          <FormField
            label="Font Family"
            error={errors.fontFamily}
          >
            <Select
              options={fontOptions}
              value={data.fontFamily}
              onChange={(value) => onChange({ fontFamily: value as DesignConfig['fontFamily'] })}
              placeholder="Select font"
              error={errors.fontFamily}
            />
          </FormField>

          <FormField
            label="Border Radius"
            error={errors.borderRadius}
          >
            <Select
              options={radiusOptions}
              value={data.borderRadius}
              onChange={(value) => onChange({ borderRadius: value as DesignConfig['borderRadius'] })}
              placeholder="Select radius"
              error={errors.borderRadius}
            />
          </FormField>

          <FormField
            label="Style Preset"
            error={errors.stylePreset}
          >
            <Select
              options={styleOptions}
              value={data.stylePreset}
              onChange={(value) => onChange({ stylePreset: value as DesignConfig['stylePreset'] })}
              placeholder="Select style"
              error={errors.stylePreset}
            />
          </FormField>
        </div>
      </div>

      <div
        style={{
          marginTop: 'var(--space-6)',
          padding: 'var(--space-6)',
          border: 'var(--border-thick)',
          backgroundColor: '#f9fafb',
        }}
      >
        <h4 style={{ marginBottom: 'var(--space-4)' }}>Preview</h4>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            style={{
              backgroundColor: data.primaryColor,
              color: getContrastTextColor(data.primaryColor),
              padding: 'var(--space-3) var(--space-6)',
              border: 'none',
              borderRadius: {
                none: '0',
                sm: '4px',
                md: '8px',
                lg: '16px',
              }[data.borderRadius],
              fontFamily: `'${data.fontFamily}', sans-serif`,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Primary Button
          </button>
          <button
            type="button"
            style={{
              backgroundColor: data.accentColor,
              color: getContrastTextColor(data.accentColor),
              padding: 'var(--space-3) var(--space-6)',
              border: 'none',
              borderRadius: {
                none: '0',
                sm: '4px',
                md: '8px',
                lg: '16px',
              }[data.borderRadius],
              fontFamily: `'${data.fontFamily}', sans-serif`,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Accent Button
          </button>
          <div
            style={{
              backgroundColor: data.secondaryColor,
              color: getContrastTextColor(data.secondaryColor),
              padding: 'var(--space-3) var(--space-6)',
              borderRadius: {
                none: '0',
                sm: '4px',
                md: '8px',
                lg: '16px',
              }[data.borderRadius],
              fontFamily: `'${data.fontFamily}', sans-serif`,
            }}
          >
            Secondary Element
          </div>
        </div>
      </div>
    </div>
  );
});

DesignConfigForm.displayName = 'DesignConfigForm';

export default DesignConfigForm;
